import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Popover, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import CommonButton from '@src/new-components/Button';
import Tooltip from '@src/components/Tooltip';
import { COLOR } from '@src/styles/color';
import { usePronunciationContext } from '@src/contexts/PronunciationCheckContext';
import usePronunciationPreview from '@src/hooks/usePronunciationPreview';
import { updatePronunciation } from '@src/redux/pronunciationCheck/actions';
import { DICTIONARY_ALLOWED_TYPES } from '@src/constants/pronunciationCheck';
import sanitize from 'sanitize-html';
import { checkValidatePronunciation } from '@src/containers/NewDictionary/utils';
import NewDictionary from '@src/containers/NewDictionary';
import { VALID_CHARACTERS_REGEX } from '@src/constants/tts';
import {
  StyledSidebar,
  StyledHeader,
  StyledLabel,
  StyledActions,
  StyledMenuContent,
} from './index.style';

const MAX_INPUT_LENGTH = 200;

const closeBtnStyle = {
  minWidth: 32,
  width: 32,
  height: 32,
  padding: '4px',
  borderRadius: '50%',
  border: 'none',
  background: 'none',
  color: COLOR.indigo[64],
  '& .MuiButton-startIcon': { margin: 0 },
  '&:hover': { background: COLOR.indigo[8] },
};

const navBtnStyle = {
  minWidth: 32,
  width: 32,
  height: 32,
  padding: '4px',
  borderRadius: '100px',
  border: 'none',
  background: 'none',
  color: COLOR.darkBlue,
  '& .MuiButton-startIcon': { margin: 0 },
  '&:hover': { background: COLOR.indigo[8] },
  '&:focus': { background: 'none' },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'auto',
  },
};

const previewBtnStyle = {
  minWidth: 32,
  width: 32,
  height: 32,
  padding: '4px',
  flexShrink: 0,
  borderRadius: '480px',
  border: 'none',
  color: COLOR.darkBlue,
  '& .MuiButton-startIcon': { margin: 0 },
  '&:hover': {
    background: COLOR.indigo[8],
  },
};

const applyBtnStyle = {
  height: 32,
  padding: '4px 12px',
  borderRadius: '8px',
  border: 'none',
  background: COLOR.primary,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { background: COLOR.primaryHover },
  '&.Mui-disabled': {
    background: COLOR.primary,
    opacity: 0.5,
  },
};

const moreBtnStyle = {
  minWidth: 32,
  width: 32,
  height: 32,
  padding: '4px',
  borderRadius: '8px',
  border: 'none',
  background: 'none',
  color: COLOR.darkBlue,
  '& .MuiButton-startIcon': { margin: 0 },
  '&:hover': { background: COLOR.indigo[8] },
  '&:focus': { background: 'none' },
};

const menuItemStyle = {
  width: '100%',
  height: 40,
  padding: '8px 12px',
  borderRadius: '8px',
  border: 'none!important',
  justifyContent: 'flex-start',
  textTransform: 'none',
  '&:hover': {
    background: COLOR.indigo[4] || 'rgba(30, 22, 78, 0.04)',
  },
};

const PronunciationSideBox = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    items,
    selectedIndex,
    currentItem,
    sameWordCount,
    applySingle,
    applyAll,
    goToNext,
    goToPrev,
    close,
    getCaptchaTokens,
    handlePreviewError,
  } = usePronunciationContext();

  const { preview, isPreviewLoading } = usePronunciationPreview({
    getCaptchaTokens,
    onError: handlePreviewError,
  });

  const [inputValue, setInputValue] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [openDictionary, setOpenDictionary] = useState(false);

  const validateInput = useCallback(
    (value) => {
      const [invalid, empty, overMaxLength] = checkValidatePronunciation(
        value?.trim(),
        MAX_INPUT_LENGTH,
      );
      if (empty) {
        setErrorMsg(t('invalidEmptyPronunciation'));
        return false;
      }
      if (invalid) {
        setErrorMsg(t('invalidPronunciationOfNewDictionary'));
        return false;
      }
      if (overMaxLength) {
        setErrorMsg(t('overMaxLengthPronunciation'));
        return false;
      }
      setErrorMsg('');
      return true;
    },
    [t],
  );

  useEffect(() => {
    const pronunciation = currentItem?.pronunciation || '';
    setInputValue(pronunciation);
    setErrorMsg('');
  }, [currentItem]);

  const handleMenuOpen = useCallback((e) => {
    setMenuAnchorEl(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const syncPronunciation = useCallback(() => {
    if (currentItem && inputValue !== currentItem.pronunciation) {
      dispatch(updatePronunciation(selectedIndex, inputValue?.trim()));
    }
  }, [currentItem, inputValue, selectedIndex, dispatch]);

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value.slice(0, MAX_INPUT_LENGTH);
      setInputValue(value);
      validateInput(value);
    },
    [validateInput],
  );

  const handleApply = useCallback(() => {
    if (!inputValue.trim() || !!errorMsg) return;
    syncPronunciation();
    applySingle(inputValue.trim());
  }, [syncPronunciation, applySingle, inputValue]);

  const handleApplyAll = useCallback(() => {
    handleMenuClose();
    syncPronunciation();
    applyAll(inputValue?.trim());
  }, [handleMenuClose, syncPronunciation, applyAll, inputValue]);

  const getPreviewIcon = () => {
    if (isPreviewLoading) return <CircularProgress size={16} color="info" />;
    return <VolumeUpIcon sx={{ width: 20, height: 20 }} />;
  };

  if (!currentItem) return null;
  const listUnApplied = items.filter((item) => !item.applied);
  const canAddToDictionary = DICTIONARY_ALLOWED_TYPES.includes(
    currentItem?.type,
  );

  const hasNext = items.some((item, i) => i > selectedIndex && !item.applied);
  const hasPrev = items.some((item, i) => i < selectedIndex && !item.applied);
  const hasHideButton = listUnApplied?.length <= 1 || currentItem?.applied;

  return (
    <StyledSidebar>
      <StyledHeader>
        <span className="title">{t('aiEditorPronunciationCheck')}</span>
        <CommonButton
          variant="text"
          startIcon={<CloseIcon fontSize="small" />}
          onClick={close}
          aria-label={t('close')}
          customStyle={closeBtnStyle}
        />
      </StyledHeader>

      <div>
        <StyledLabel>
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitize(
                t('pronunciationFor', { word: currentItem.word }),
                {
                  allowedTags: ['strong'],
                },
              ),
            }}
          />
          <Tooltip
            arrow
            title={t('preview')}
            placement="top"
            disableInteractive
          >
            <span>
              <CommonButton
                variant="text"
                startIcon={getPreviewIcon()}
                onClick={() => {
                  const isValidText = VALID_CHARACTERS_REGEX.test(
                    currentItem?.word,
                  );

                  if (!isValidText) {
                    preview(inputValue);
                  } else {
                    const nswItem = currentItem
                      ? [
                          {
                            orig: currentItem.word,
                            pronunciation: inputValue,
                            position: {
                              start: 0,
                              end: currentItem.word.length,
                            },
                            type: currentItem.type,
                          },
                        ]
                      : undefined;
                    preview(currentItem?.word || inputValue, {
                      nsw: nswItem,
                    });
                  }
                }}
                disabled={isPreviewLoading || !inputValue.trim() || !!errorMsg}
                aria-label={t('preview')}
                customStyle={previewBtnStyle}
              />
            </span>
          </Tooltip>
        </StyledLabel>

        <TextField
          multiline
          rows={3}
          value={inputValue}
          onChange={handleInputChange}
          error={!!errorMsg}
          helperText={errorMsg || undefined}
          InputProps={{
            inputProps: { maxLength: MAX_INPUT_LENGTH },
            endAdornment: (
              <span
                style={{
                  position: 'absolute',
                  right: 12,
                  bottom: 8,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: 'rgba(28, 28, 30, 0.48)',
                  pointerEvents: 'none',
                }}
              >
                {inputValue.length}/{MAX_INPUT_LENGTH}
              </span>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              position: 'relative',
              padding: '12px',
              paddingBottom: '28px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              color: COLOR.darkBlue,
              background: '#fff',
              '& fieldset': {
                borderWidth: '0.5px',
                borderColor: COLOR.indigo[16],
              },
              '&:hover fieldset': {
                borderColor: COLOR.indigo[16],
              },
              '&.Mui-focused fieldset': {
                borderWidth: '0.5px',
                borderColor: COLOR.blueV2,
              },
              '&.Mui-error fieldset': {
                borderColor: COLOR.redPrimary,
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: 0,
            },
            '& .MuiFormHelperText-root': {
              fontSize: '12px',
              lineHeight: '16px',
              marginLeft: 0,
              marginRight: 0,
              '&.Mui-error': {
                color: COLOR.redPrimary,
              },
            },
          }}
          fullWidth
        />
      </div>

      <StyledActions>
        <div className="nav-group">
          {!hasHideButton && (
            <Tooltip arrow title={t('previous')} placement="bottom">
              <span>
                <CommonButton
                  variant="text"
                  startIcon={<ChevronLeftIcon />}
                  onClick={goToPrev}
                  disabled={!hasPrev}
                  customStyle={navBtnStyle}
                />
              </span>
            </Tooltip>
          )}
          {!hasHideButton && (
            <Tooltip arrow title={t('nextV2')} placement="bottom">
              <span>
                <CommonButton
                  variant="text"
                  startIcon={<ChevronRightIcon />}
                  onClick={goToNext}
                  disabled={!hasNext}
                  customStyle={navBtnStyle}
                />
              </span>
            </Tooltip>
          )}
        </div>

        <div className="action-group">
          <CommonButton
            variant="contained"
            onClick={handleApply}
            customStyle={applyBtnStyle}
            textProps={{
              variantText: 'body-14-medium',
              color: COLOR.darkBlue,
            }}
          >
            {t('apply')}
          </CommonButton>

          <CommonButton
            variant="text"
            startIcon={<MoreVertIcon />}
            onClick={handleMenuOpen}
            customStyle={moreBtnStyle}
          />
          <Popover
            open={Boolean(menuAnchorEl)}
            anchorEl={menuAnchorEl}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                borderRadius: '8px',
                boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
                marginTop: '4px',
              },
            }}
          >
            <StyledMenuContent>
              <CommonButton
                variant="outlined"
                onClick={handleApplyAll}
                customStyle={menuItemStyle}
                textProps={{
                  variantText: 'body-14-medium',
                  color: COLOR.darkBlue,
                }}
              >
                {t('applyAll', { count: sameWordCount })}
              </CommonButton>
              <Tooltip
                arrow
                title={
                  canAddToDictionary ? '' : t('addToDictionaryDisabledTooltip')
                }
                placement="left"
                disableHoverListener={canAddToDictionary}
              >
                <span>
                  <CommonButton
                    variant="outlined"
                    disabled={!canAddToDictionary}
                    onClick={() => {
                      setOpenDictionary(true);
                      handleMenuClose();
                    }}
                    customStyle={menuItemStyle}
                    textProps={{
                      variantText: 'body-14-medium',
                      color: COLOR.darkBlue,
                    }}
                  >
                    {t('addToDictionary')}
                  </CommonButton>
                </span>
              </Tooltip>
            </StyledMenuContent>
          </Popover>
        </div>
      </StyledActions>

      {openDictionary && (
        <NewDictionary
          open={openDictionary}
          onClose={() => setOpenDictionary(false)}
          currentItem={{ ...currentItem, pronunciation: inputValue }}
        />
      )}
    </StyledSidebar>
  );
};

export default PronunciationSideBox;
